import { useMemo } from 'react';
import { CandidateRow } from '@shared/models/element-import.model';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useProjectState } from '@client/store/project';
import { useElementSearchString } from '@client/hooks/use-filter-elements.hooks';
import { useExpandedItems } from '@client/hooks/use-expand-elements.hooks';
import { MappingsByCode } from './element-imports.store';
import { useElementImports } from './use-element-imports.hook';
import { getDiscardTriggerByRowId } from '@shared/helpers/candidate-discard.helpers';
import {
  useImportedElements,
  resolveCandidateProduct,
  resolveCandidateRecipe,
  resolveCandidateStatus,
} from './candidate-status.helpers';
import { matchesCandidateSearch } from './candidate-search.helpers';
import { buildDraftMappingsByRowId } from '@shared/helpers/import-row-draft.helpers';
import {
  CSV_IMPORT_EXPAND_PREFIX,
  getImportsNewestFirst,
  resolveSearchScopeImportId,
} from './import-list-navigation.helpers';

export type CandidateSearchFilter = {
  /** Active search query (URL param). Empty string = no filtering. */
  search: string;
  /** Whether filtering is currently narrowing results. */
  isActive: boolean;
  /** Predicate matching a row against {@link search}; `true` when no query. */
  matches: (row: CandidateRow) => boolean;
  /** File Cmd+F filters; other files stay unfiltered. */
  scopeImportId: string | undefined;
};

const emptyMappings: MappingsByCode = {};

/**
 * Build a memoized predicate that matches candidate rows against the shared
 * element-list search query. Cmd+F on Import is scoped to
 * {@link CandidateSearchFilter.scopeImportId}; callers must only apply
 * {@link CandidateSearchFilter.matches} to that file.
 */
export const useCandidateSearchFilter = (
  projectId: number,
): CandidateSearchFilter => {
  const [search] = useElementSearchString();
  const version = useSelectedVersion();
  const selectedElementId = useSelectedElementId();
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const imports = useElementImports(projectId);
  const { is: isImportExpanded } = useExpandedItems({
    defaultPrefix: CSV_IMPORT_EXPAND_PREFIX,
  });
  const mappingsByCode =
    imports.status === 'ready' ? imports.data.mappingsByCode : emptyMappings;

  const importedElements = useImportedElements(version);
  const importsById = useMemo(
    () => (imports.status === 'ready' ? imports.data.importsById : {}),
    [imports],
  );

  const allRows = useMemo(
    () =>
      imports.status === 'ready'
        ? Object.values(imports.data.rowsByImport).flat()
        : [],
    [imports],
  );

  const matches = useMemo(() => {
    if (!search.trim()) return () => true;
    const discardTriggerByRowId = getDiscardTriggerByRowId(version);
    const draftMappingsByRowId = buildDraftMappingsByRowId(version, allRows);
    return (row: CandidateRow) => {
      // Mirror the table: only materialized rows resolve their mapping by code.
      // Rows without an element never consult the DB mapping (no pre-fill).
      const mappingsForRow = importedElements.has(row.id)
        ? mappingsByCode
        : emptyMappings;
      const status = resolveCandidateStatus(
        row,
        mappingsForRow,
        discardTriggerByRowId,
        productsById,
        importedElements,
        { importsById, draftMappingsByRowId, recipesById },
      );
      const product = resolveCandidateProduct(
        row,
        mappingsForRow,
        productsById,
        draftMappingsByRowId[row.id],
      );
      const recipe = resolveCandidateRecipe(
        row,
        mappingsForRow,
        recipesById,
        draftMappingsByRowId[row.id],
      );
      return matchesCandidateSearch(row, search, {
        productName: product?.name ?? recipe?.name,
        status,
      });
    };
  }, [
    search,
    mappingsByCode,
    version,
    productsById,
    recipesById,
    importedElements,
    importsById,
    allRows,
  ]);

  const scopeImportId = useMemo(
    () =>
      resolveSearchScopeImportId({
        selectedId: selectedElementId,
        imports: getImportsNewestFirst(
          importsById,
          version?.imports?.importIds,
        ),
        rowsByImport:
          imports.status === 'ready' ? imports.data.rowsByImport : {},
        importedElements,
        isImportExpanded,
      }),
    [
      selectedElementId,
      importsById,
      version?.imports?.importIds,
      imports,
      importedElements,
      isImportExpanded,
    ],
  );

  return {
    search,
    isActive: search.trim().length > 0,
    matches,
    scopeImportId,
  };
};

/**
 * Total number of candidate rows across all imports that currently match the
 * shared search query. Used by `ElementsView` to decide whether the search
 * field should auto-close on click-outside while the Imports tab is active.
 */
export const useImportSearchMatchCount = (): number => {
  const { project } = useProjectState('project');
  const projectId = project?.id ?? -1;
  const imports = useElementImports(projectId);
  const { matches, isActive, scopeImportId } =
    useCandidateSearchFilter(projectId);

  return useMemo(() => {
    if (!project?.id || imports.status !== 'ready') return 0;
    const rowsByImport = imports.data.rowsByImport;
    const scopedRows = scopeImportId
      ? (rowsByImport[scopeImportId] ?? [])
      : Object.values(rowsByImport).flat();
    if (!isActive) return scopedRows.length;
    return scopedRows.filter(matches).length;
  }, [project?.id, imports, matches, isActive, scopeImportId]);
};
